import type {EvaluatedParameters, Evaluator} from "#graph/evaluators/types"
import type {NodeParameters} from "#graph/nodes/base/types"
import {GraphNode} from "#graph/nodes/graph/graph-node"
import type {ParameterValue} from "#graph/nodes/graph/helpers"
import {ensureValidResult} from "#graph/nodes/graph/validation"
import type {PromisesMap} from "@cm/utils"
import {mapFields, promiseAllProperties} from "@cm/utils"

export class UncachedEvaluator<Context> implements Evaluator<Context> {
    public constructor(
        public readonly context: Context,
        protected disableValidation = false,
    ) {}

    get = async <ReturnType>(value: ParameterValue<ReturnType>): Promise<ReturnType> => {
        if (value instanceof GraphNode) {
            return (await this.evaluateGraph(value)) as ReturnType
        }
        return value as ReturnType
    }

    getSync = <ReturnType>(value: ParameterValue<ReturnType, Context>): ReturnType => {
        if (value instanceof GraphNode) {
            return this.evaluateGraphSync(value) as ReturnType
        }
        return value
    }

    protected async evaluateGraph(graph: GraphNode<unknown, Context>) {
        if (!this.disableValidation) {
            graph.ensureValidParameters()
            const computedValue = await graph.run(this)
            return ensureValidResult(graph, computedValue)
        }
        return graph.run(this)
    }

    getAll = async <Parameters extends NodeParameters>(parameters: Parameters): Promise<EvaluatedParameters<Parameters, Context>> => {
        return promiseAllProperties(mapFields(parameters, (value) => this.get(value)) as PromisesMap<Parameters>) as Promise<
            EvaluatedParameters<Parameters, Context>
        >
    }

    getAllSync = <Parameters extends NodeParameters>(parameters: Parameters): EvaluatedParameters<Parameters, Context> => {
        return mapFields(parameters, (value) => this.getSync(value)) as EvaluatedParameters<Parameters, Context>
    }

    protected evaluateGraphSync(graph: GraphNode<unknown, Context>) {
        if (!this.disableValidation) {
            graph.ensureValidParameters()
            const computedValue = graph.runSync(this)
            return ensureValidResult(graph, computedValue)
        }
        return graph.runSync(this)
    }

    withContext = <OtherContext extends Context>(context: OtherContext): Evaluator<OtherContext> => {
        return new UncachedEvaluator(context, this.disableValidation)
    }
}
